import clsx from "clsx";
import { components, type GroupBase, type OptionProps } from "react-select";
import SelectWithCreatableOptions from "react-select/creatable";
import { twMerge } from "tailwind-merge";

interface ChannelOption {
  label: string;
  value: string;
  showWarningOnSelected?: boolean;
}

interface Props {
  isLoading?: boolean;
  selectedChannels: ChannelOption["value"][];
  initialOptions: ChannelOption[];
  onChange(selectedChannels: ChannelOption["value"][]): void;
  allowCreatingOptions?: boolean;
  warningMessage?: string | React.ReactElement;
  WarningMessageContainer?: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
  }>;
  className?: string;
  menuListClassName?: string;
}

export function SupportedChannelsSelector({
  isLoading = false,
  selectedChannels,
  initialOptions,
  onChange,
  warningMessage,
  WarningMessageContainer,
  className,
  menuListClassName,
}: Props) {
  const selectedChannelsAsOptions = selectedChannels.map((channel) => {
    const option = initialOptions.find(
      (initialOption) => initialOption.value === channel,
    );
    return option ?? { label: channel, value: channel };
  });

  const optionsToShowWarningWhenSelected = initialOptions
    .filter((option) => option.showWarningOnSelected)
    .map((option) => option.value);

  const showWarningMessage = selectedChannels.some((selectedChannel) =>
    optionsToShowWarningWhenSelected.includes(selectedChannel),
  );

  const warningMessageWithIcon = warningMessage && (
    <>
      <i className="fa-solid fa-triangle-exclamation text-mars+1 mr-1" />
      {warningMessage}
    </>
  );

  const warningMessageWithTooltip = WarningMessageContainer ? (
    <WarningMessageContainer className="flex gap-1 items-center pl-4 text-sm italic">
      {warningMessageWithIcon}
    </WarningMessageContainer>
  ) : (
    warningMessageWithIcon
  );

  return (
    <div className={twMerge("space-y-1", className)}>
      <SelectWithCreatableOptions
        menuPortalTarget={document.body}
        options={initialOptions}
        value={selectedChannelsAsOptions}
        isLoading={isLoading}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        components={{
          DropdownIndicator: (props) => (
            <components.DropdownIndicator {...props}>
              <i
                className={clsx(
                  props.selectProps.menuIsOpen
                    ? "fa-chevron-up"
                    : "fa-chevron-down",
                  "fa-solid text-[0.6rem] text-gray-600 px-2",
                )}
              />
            </components.DropdownIndicator>
          ),
          ClearIndicator: undefined,
          MultiValueContainer: (props) => (
            <components.MultiValueContainer {...props}>
              <div className="flex justify-center items-center">
                {props.data.showWarningOnSelected && (
                  <i className="fa-solid fa-triangle-exclamation text-mars+1" />
                )}
                {props.children}
              </div>
            </components.MultiValueContainer>
          ),
          MultiValueRemove: (props) => (
            <components.MultiValueRemove {...props}>
              <i className="fa-regular fa-xmark text-[0.8rem] text-gray-700 pt-0.5" />
            </components.MultiValueRemove>
          ),
          Option: CheckboxOption,
        }}
        classNames={{
          control: () => "max-h-[120px] overflow-y-auto shadow-none rounded-md",
          input: () => "text-xs font-light text-blue-500",
          multiValue: (props) =>
            twMerge(
              "bg-[#F5F8FE] border border-cosmic-2 rounded-2xl text-xs font-light px-1.5 items-center text-red-500",
              props.data.showWarningOnSelected &&
                "bg-mars-2 bg-opacity-20 border border-mars-1",
            ),
          multiValueLabel: () => "text-blue-500",
          multiValueRemove: () => "bg-transparent text-black ml-0.5",
          indicatorSeparator: () => "hidden",
          menuList: () =>
            twMerge("xl:max-h-[180px] max-h-[120px]", menuListClassName),
          option: (props) =>
            props.isFocused
              ? "bg-[#F5F7FDB2] text-xs font-light text-black"
              : "bg-[#fff] text-xs font-light text-black",
        }}
        isClearable
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        placeholder="Select the channels you want to connect"
        onChange={(channels) => {
          onChange(channels.map((channel) => channel.value));
        }}
        noOptionsMessage={() => "No options"}
        isValidNewOption={() => false}
      />
      {showWarningMessage && warningMessageWithTooltip}
    </div>
  );
}

export function CheckboxOption<
  T extends ChannelOption,
  IsMulti extends boolean,
  Group extends GroupBase<T>,
>({
  children,
  isSelected,
  ...rest
}: OptionProps<ChannelOption, IsMulti, Group>) {
  const { data } = rest;
  return (
    <components.Option {...rest} isSelected={isSelected}>
      <div className="flex items-center">
        <input
          type="checkbox"
          readOnly
          checked={isSelected}
          className="mr-2 w-3 h-3"
        />
        {data.showWarningOnSelected && (
          <i className="mr-1 fa-solid fa-triangle-exclamation text-mars+1"></i>
        )}
        {children}
      </div>
    </components.Option>
  );
}
